import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";

const BusinessRegistrationDocuments = ({
  formData,
  setFormData,
  vatPreview,
  setVatPreview,
  crPreview,
  setCrPreview,
  vatFileName,
  setVatFileName,
  crFileName,
  setCrFileName,
}) => {
  const { t } = useTranslation();

  const InputWrapper = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    border: "2px solid",
    marginTop: theme.spacing(2),
    borderColor: "#00ccbb",
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      borderColor: "#009f94",
    },
  }));

  const HiddenInput = styled("input")({
    display: "none",
  });

  const CustomButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(1),
    padding: theme.spacing(1, 2),
    backgroundColor: "#00ccbb",
    "&:hover": {
      backgroundColor: "#009f94",
    },
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    if (!file) return;

    const fileUrl = URL.createObjectURL(file);

    if (name === "vat_image") {
      setVatFileName(file.name);
      setVatPreview({ url: fileUrl, type: file.type });
    } else if (name === "cr_image") {
      setCrFileName(file.name);
      setCrPreview({ url: fileUrl, type: file.type });
    }

    setFormData({ ...formData, [name]: file });
  };

  const removeFile = (type) => {
    if (type === "vat") {
      URL.revokeObjectURL(vatPreview.url);
      setVatPreview({ url: null, type: null });
      setVatFileName("");
      setFormData({ ...formData, vat_image: null });
    } else if (type === "cr") {
      URL.revokeObjectURL(crPreview.url);
      setCrPreview({ url: null, type: null });
      setCrFileName("");
      setFormData({ ...formData, cr_image: null });
    }
  };

  const renderPreview = (preview, type) => {
    if (!preview.url) return null;

    const isImage = preview.type.startsWith("image/");
    const isPdf = preview.type === "application/pdf";

    return (
      <Box
        mt={2}
        display="flex"
        alignItems="center"
        gap={2}
        flexDirection="row"
      >
        {isImage && (
          <img
            src={preview.url}
            alt="Preview"
            style={{
              maxWidth: 200,
              maxHeight: 150,
              objectFit: "cover",
              borderRadius: 8,
            }}
          />
        )}
        {isPdf && (
          <Button
            variant="outlined"
            color="primary"
            href={preview.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Preview PDF
          </Button>
        )}
        <IconButton onClick={() => removeFile(type)} color="error">
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  useEffect(() => {
    if (formData.vat_image) {
      const fileUrl = URL.createObjectURL(formData.vat_image);
      setVatPreview({ url: fileUrl, type: formData.vat_image.type });
      setVatFileName(formData.vat_image.name);
    }

    if (formData.cr_image) {
      const fileUrl = URL.createObjectURL(formData.cr_image);
      setCrPreview({ url: fileUrl, type: formData.cr_image.type });
      setCrFileName(formData.cr_image.name);
    }

    return () => {
      // cleanup URLs to avoid memory leaks
      if (vatPreview?.url) URL.revokeObjectURL(vatPreview.url);
      if (crPreview?.url) URL.revokeObjectURL(crPreview.url);
    };
  }, []);


  return (
    <>
      <TextField
        name="vat"
        label={t("vat_number")}
        value={formData.vat}
        onChange={handleChange}
        variant="outlined"
        fullWidth
        required
      />
      <br />
      <InputWrapper>
        <HiddenInput
          type="file"
          name="vat_image"
          onChange={handleFileChange}
          id="vat-upload"
        />
        <label htmlFor="vat-upload">
          <CustomButton variant="contained" color="primary" component="span">
            {t("upload_vat") + "*"}
          </CustomButton>
        </label>
        {vatFileName && (
          <Typography variant="body2" color="textSecondary" mt={2}>
            Selected file: {vatFileName}
          </Typography>
        )}
      </InputWrapper>
      {renderPreview(vatPreview, "vat")}
      <br />
      <TextField
        type="number"
        name="cr"
        label={t("cr")}
        value={formData.cr}
        onChange={handleChange}
        variant="outlined"
        fullWidth
        required
      />
      <InputWrapper>
        <HiddenInput
          type="file"
          name="cr_image"
          onChange={handleFileChange}
          id="cr-upload"
        />
        <label htmlFor="cr-upload">
          <CustomButton variant="contained" color="primary" component="span">
            {t("upload_cr") + "*"}
          </CustomButton>
        </label>
        {crFileName && (
          <Typography variant="body2" color="textSecondary" mt={2}>
            Selected file: {crFileName}
          </Typography>
        )}
      </InputWrapper>
      {renderPreview(crPreview, "cr")}
    </>
  );
};

export default BusinessRegistrationDocuments;
